import React, { useState, useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
 Modal,
 ModalContent,
 ModalOverlay,
 Flex,
 Button,
 Text,
 Stack,
 RadioGroup,
 Radio,
 Checkbox,
 useToast,
 Box,
 useClipboard,
 Tooltip,
 IconButton,
 Icon,
} from '@chakra-ui/react';
import { FiCopy, FiCheck, FiInfo } from 'react-icons/fi';
import baseService from '../../services/base';
import SuccessModal from './SuccessModal';
import { InputText } from '../Input';
import { fetchData } from '../../services/fetch';

// Senha padrão criada pelo backend (DEFAULT_IDP_PASSWORD). O usuário a troca no
// primeiro acesso. Mesmo valor exibido no app do respondente (gs-form-survey-ui).
const DEFAULT_PASSWORD = 'GP@esg!2024';

const schema = yup.object().shape({
 name: yup.string().required('Campo obrigatório'),
 email: yup.string().required('Campo obrigatório').email('E-mail inválido'),
 userType: yup.string().required('Campo obrigatório'),
 userProfile: yup
  .array()
  .test('profile-validation', 'Selecione pelo menos um perfil', function (value) {
   if (!value || !value.length) return false;
   return value.length > 0;
  }),
});

const profileOptions = {
 EMPRESA: ['RESPONDENTE', 'RESPONSAVEL', 'ADMIN'],
 INTERNO: ['MASTER', 'AVALIADOR'],
};

// RESPONSÁVEL e ADMIN usam a mesma role RESPONSAVEL (3); o que difere é o vínculo
// links_admin_company (admin da empresa), enviado via flag `admin` no payload.
const PROFILE_TO_ROLE_NAME = { ADMIN: 'RESPONSAVEL' };

export default function UserAccessModal({ onClose, isOpen, companyGroupId }) {
 const { mutate } = useSWRConfig();
 const toast = useToast();
 const [success, setSuccess] = useState(false);

 const { data: roles } = useSWR(isOpen ? `/roles` : null, fetchData);

 // Lista do grupo p/ barrar e-mail duplicado (mesma key da UserTable -> cache compartilhado).
 const { data: groupUsers } = useSWR(
  isOpen && companyGroupId ? `/user?group=${companyGroupId}` : null,
  fetchData
 );

 const { onCopy, hasCopied } = useClipboard(DEFAULT_PASSWORD);

 const getRoleId = (profileName) => {
  const role = roles?.find((role) => role.name === profileName);
  return role?.id;
 };

 const {
  handleSubmit,
  register,
  reset,
  setValue,
  setError,
  watch,
  formState: { errors, isValid, isSubmitting },
 } = useForm({
  resolver: yupResolver(schema),
  defaultValues: {
   name: '',
   email: '',
   userType: 'EMPRESA',
   userProfile: ['RESPONDENTE'],
  },
 });

 const userType = watch('userType');
 const userProfile = watch('userProfile') || [];

 // O 1º usuário da empresa (grupo ainda sem usuários) entra como ADMIN.
 const isFirstUser = (groupUsers?.length ?? 0) === 0;

 useEffect(() => {
  if (userType === 'EMPRESA') {
   setValue('userProfile', isFirstUser ? ['ADMIN'] : ['RESPONDENTE'], {
    shouldValidate: true,
   });
  } else {
   setValue('userProfile', [], { shouldValidate: true });
  }
 }, [userType, isFirstUser, setValue]);

 const handleProfileChange = (profile) => {
  const currentProfiles = userProfile || [];
  const isSelected = currentProfiles.includes(profile);
  let newProfiles;

  if (isSelected) {
   newProfiles = currentProfiles.filter((p) => p !== profile);
  } else {
   newProfiles = [...currentProfiles, profile];
  }

  setValue('userProfile', newProfiles, { shouldValidate: true });
 };

 const onSubmit = async (data) => {
  try {
   const { name, userProfile } = data;
   const email = data.email.toLowerCase().trim();

   // Bloqueio de e-mail duplicado: o backend POST /user não rejeita e-mail
   // repetido (mescla roles no usuário existente quando não há conflito de
   // role), então barramos aqui contra a lista do grupo já carregada.
   const alreadyExists = (groupUsers || []).some(
    (u) => u.email?.toLowerCase().trim() === email
   );
   if (alreadyExists) {
    setError('email', {
     type: 'manual',
     message: 'Já existe um usuário com este e-mail.',
    });
    return;
   }

   const rolesIds = [
    ...new Set(
     userProfile
      .map((profile) => getRoleId(PROFILE_TO_ROLE_NAME[profile] || profile))
      .filter(Boolean),
    ),
   ];

   const payload = {
    username: name,
    email,
    id_company_group: companyGroupId,
    roles: rolesIds,
    // Só vira admin da empresa (links_admin_company) quando o perfil ADMIN é
    // escolhido; RESPONSÁVEL sozinho é responsável e nada mais.
    admin: userProfile.includes('ADMIN'),
   };

   const response = await baseService.post('/user', payload);
   if (response.status === 201) {
    setSuccess(true);
    mutate(`/user?group=${companyGroupId}`);
   }
  } catch (err) {
   toast({
    title: 'Erro ao adicionar o usuário',
    description: 'Tente novamente',
    status: 'error',
   });
  }
 };

 const handleClose = () => {
  onClose();
  setSuccess(false);
  reset();
 };

 return success ? (
  <SuccessModal
   isOpen={isOpen}
   onClose={handleClose}
   title='Usuário adicionado com sucesso'
  />
 ) : (
  <Modal isOpen={isOpen} onClose={handleClose} size='4xl'>
   <ModalOverlay />
   <ModalContent
    as='form'
    onSubmit={handleSubmit(onSubmit)}
    width='808px'
    borderRadius='12px'
    p='32px'
   >
    <Text fontSize='24px' lineHeight='28.18px' fontWeight='700' mb='32px'>
     Cadastrar usuário
    </Text>

    <Stack spacing='32px'>
     <Flex gap='4'>
      <InputText
       {...register('name')}
       error={errors.name}
       label='Nome'
       placeholder='Digite o nome do usuário'
      />
      <InputText
       type='email'
       {...register('email')}
       error={errors.email}
       label='E-mail'
       placeholder='Digite o e-mail do usuário'
      />
     </Flex>

     <Flex gap='4' mt='-8px'>
      <Box>
       <Text fontSize='12px' lineHeight='12.09px' fontWeight='600' mb='8px'>
        USUÁRIO
       </Text>
       <RadioGroup
        onChange={(value) => setValue('userType', value, { shouldValidate: true })}
        value={userType}
       >
        <Flex gap='12px' className='questionary-app'>
         <Radio
          value='EMPRESA'
          colorScheme='blue'
          _checked={{
           bg: 'blue.500',
           color: 'white',
          }}
         >
          <Text as='span' fontSize='sm' lineHeight='32px'>
           EMPRESA
          </Text>
         </Radio>
         <Radio
          value='INTERNO'
          _checked={{
           bg: 'blue.500',
           color: 'white',
          }}
         >
          INTERNO
         </Radio>
        </Flex>
       </RadioGroup>
      </Box>

      <Box>
       <Text fontSize='12px' lineHeight='12.09px' fontWeight='600' mb='8px'>
        PERFIL
       </Text>
       <Flex h='24px' align='center' gap='16px' className='regular-checkbox'>
        {profileOptions[userType].map((profile) => (
         <Checkbox
          key={profile}
          isChecked={userProfile.includes(profile)}
          onChange={() => handleProfileChange(profile)}
          colorScheme='blue'
         >
          <Text as='span' lineHeight='26px' color='#292B30'>
           {profile}
          </Text>
         </Checkbox>
        ))}
       </Flex>
       {/* {errors.userProfile && (
        <Text color='red.500' fontSize='sm' mt='4px'>
         {errors.userProfile.message}
        </Text>
       )} */}
      </Box>
     </Flex>

     <Box
      borderWidth='1px'
      borderColor='blue.100'
      bg='blue.50'
      borderRadius='lg'
      p='16px'
     >
      <Text
       fontSize='11px'
       fontWeight={700}
       color='blue.600'
       textTransform='uppercase'
       letterSpacing='0.05em'
       mb='8px'
      >
       Senha padrão
      </Text>
      <Flex
       align='center'
       justify='space-between'
       gap='8px'
       bg='white'
       borderWidth='1px'
       borderColor='blue.100'
       borderRadius='md'
       pl='12px'
       pr='4px'
       py='4px'
       maxW='320px'
      >
       <Text
        fontFamily='mono'
        fontSize='md'
        fontWeight={700}
        color='gray.800'
        letterSpacing='0.02em'
       >
        {DEFAULT_PASSWORD}
       </Text>
       <Tooltip
        label={hasCopied ? 'Copiado!' : 'Copiar senha'}
        closeOnClick={false}
        hasArrow
       >
        <IconButton
         aria-label='Copiar senha'
         icon={<Icon as={hasCopied ? FiCheck : FiCopy} />}
         size='sm'
         variant='ghost'
         colorScheme={hasCopied ? 'green' : 'blue'}
         onClick={onCopy}
        />
       </Tooltip>
      </Flex>
      <Flex mt='12px' gap='8px' align='flex-start' color='gray.600'>
       <Icon as={FiInfo} mt='2px' boxSize='14px' color='blue.400' flexShrink={0} />
       <Text fontSize='xs' lineHeight='short'>
        No primeiro login o usuário será obrigado a trocar a senha.
       </Text>
      </Flex>
     </Box>

     <Flex gap='6'>
      <Button
       variant='outline'
       color='red.500'
       borderColor='red.500'
       flex='1'
       height='55px'
       onClick={handleClose}
      >
       Cancelar
      </Button>
      <Button
       type='submit'
       isDisabled={!isValid}
       isLoading={isSubmitting}
       flex='1'
       height='55px'
       variant='primary'
      >
       Cadastrar
      </Button>
     </Flex>
    </Stack>
   </ModalContent>
  </Modal>
 );
}
